import React, { useEffect } from 'react';
import '../styles/SecurityPage.css';

import icon3 from '../assets/security-Images/icon4.svg';
import icon4 from '../assets/security-Images/icon3.png';
import icon2 from '../assets/security-Images/icon2.svg';
import icon1 from '../assets/security-Images/icon1.svg';
import icon5 from '../assets/security-Images/icon5.svg';
import image1 from '../assets/security-Images/image1.png';
import image2 from '../assets/security-Images/image2.png';
import ContactButton from './ContactButton';
import PathProgressScroller from '../hooks/svgpathbuilde';
import PageHeader from './PageHeader';
import alert from '../assets/security-Images/alert.png'
import desktop from '../assets/security-Images/desktop.png'
import search from '../assets/security-Images/search.png'
import Lock from '../assets/security-Images/Lock.png'
import activeAlert from '../assets/security-Images/activeAlert.svg'
import activeDesktop from '../assets/security-Images/activeDesktop.svg'
import activeSearch from '../assets/security-Images/activeSearch.svg'
import activeLock from '../assets/security-Images/activelock.svg'


const SAMPLE_PATHS = {
  custom: "M1 229.5C43.8333 244.167 147 275.4 205 261C277.5 243 290.5 210 297 174.5C303.494 139.034 330.5 20 397 18C463.5 16 491.5 51 536 85C580.5 119 620 145 669 146.5C718 148 746 88 718.5 67.5C691 47 644.5 57 624.5 90.5C609.079 116.33 583.5 231 749 228.5C844.077 227.064 899.041 128.725 952 76C991.226 36.9474 1002.5 17.5 1063 1.5",
  curved: "M 50 100 Q 200 50 350 100 T 650 100",
  wave: "M 50 100 Q 150 50 250 100 T 450 100 T 650 100",
  zigzag: "M 50 100 L 150 50 L 250 100 L 350 50 L 450 100 L 550 50 L 650 100",
  spiral: "M 350 100 Q 300 80 280 100 Q 260 120 280 140 Q 320 160 360 140 Q 400 120 380 100 Q 360 80 340 100"
};

// Security process steps data
const securitySteps = [
  {
    id: 1,
    title: "Threat Assessment",
    description: "We continuously monitor for emerging threats and vulnerabilities, performing regular risk assessments to identify potential security gaps in our systems and applications.",
    position: 0.1,
    icon: alert,
    activeicon: activeAlert,
    cardLeft: 3,
    cardTop: 8,
  },
  {
    id: 2,
    title: "Security Testing",
    description: "Our applications undergo rigorous security testing including static code analysis, dynamic application scanning, and regular penetration tests performed by certified security professionals.",
    position: 0.35,
    icon: Lock,
    activeicon: activeLock,
    cardLeft: 31,
    cardTop: 32,
  },
  {
    id: 3,
    title: "Continuous Monitoring",
    description: "We employ advanced monitoring tools and automated alerts to detect and respond to suspicious activities and potential security incidents in real-time across all our systems.",
    position: 0.7,
    icon: desktop,
    activeicon: activeDesktop,
    cardLeft: 56,
    cardTop: 75,
  },
  {
    id: 4,
    title: "Regular Audits",
    description: "We conduct regular internal and external security audits to verify compliance with security policies, industry standards, and regulatory requirements, ensuring continued protection of your data.",
    position: 0.95,
    icon: search,
    activeicon: activeSearch,
    cardLeft: 85,
    cardTop: 30,
  }
];

const SecurityPage: React.FC = () => {
  useEffect(() => {
    // Scroll to top when page loads
    window.scrollTo(0, 0);
  }, []);

  const certifications = [
    {
      title: "SOC 2",
      description:
        "Our platform adheres to SOC 2 standards, ensuring we maintain the highest levels of security and privacy controls. We undergo regular third-party audits to verify compliance with security, availability, and confidentiality principles established by the American Institute of CPAs (AICPA).",
      icon: icon1,
    },
    {
      title: "HIPAA",
      description:
        "We prioritize health information security and comply with HIPAA regulations. Our systems are designed to safeguard protected health information (PHI) through comprehensive access controls, encryption, audit logging, and secure data transfer mechanisms, providing reliable services to healthcare organizations.",
        icon: icon2,
      },
    {
      title: "GDPR",
      description:
        "Our platform is fully compliant with the EU’s General Data Protection Regulation (GDPR). We implement privacy by design principles, maintain data processing records, enable data subject rights, and ensure lawful data transfer mechanisms to protect the privacy rights of EU citizens.",
        icon: icon3,
      },
    {
      title: "ISO 27001",
      description:
        "ReventLabs maintains ISO 27001 certification, demonstrating our commitment to information security management. This certification verifies that we have implemented a systematic approach to managing sensitive information and ensuring business continuity through comprehensive risk management practices.",
        icon: icon4,
      },
    {
      title: "PCI DSS",
      description:
        "Our solutions are engineered to meet the Payment Card Industry Data Security Standard (PCI DSS). We safeguard cardholder data through network segmentation, strong encryption, tokenization, and strict access controls, enabling businesses to process, store, and transmit payment information securely and with confidence.",
        icon: icon5,
      },
  ];

  return (
    <div className="security-page">
      {/* Security & Compliance */}
      <PageHeader heading='Security & Compliance' text='At ReventLabs, we implement rigorous security measures to protect your data and applications. Our comprehensive approach to security ensures that our systems meet the highest industry standards and compliance requirements.'/>
      {/* Compliance Section */}
      <section className="compliance-section">
        <div className='compliance'>
          <p className="sections-title">Compliance & Certifications</p>
          <div className="certifications-container">
            {certifications.map((cert, index) => (
              <div className="cert-card" key={index}>
                <div className='cert-card-heading'>
                <p className="cert-title">{cert.title}</p>
                <div className="cert-icon">
                  <img src={cert.icon} alt={`${cert.title} compliance`}></img>
                </div>
                </div>
                <p className="cert-desc">{cert.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    
      {/* security Principle Section */}
      <div className='security-principle-section'>
        <div className='principle-f'>
        <p className="sections-title-f sections-title">Our Security Principles</p>
        <div className="security-principle">
          <div>
          <img src={image1} alt="people" />
          </div>
            <div className="security-text">
              <div className="security-principle-steps">
                <div className="security-principle-step first-step">
                  <div className="security-step-number">01</div>
                  <div className='security-step-text'>
                    <h4 className="security-step-title">Infrastructure Security</h4>
                    <p className="security-step-description">
                    Our infrastructure is built on secure cloud platforms with comprehensive access controls, network 
                    segmentation, intrusion detection systems, and 24/7 monitoring to protect against unauthorized access.
                    </p>
                  </div>
                </div>
                
                <div className="gradient-hr-f"></div>

                <div className="security-principle-step">
                  <div className="security-step-number">02</div>
                    <div className='security-step-text'>
                      <h4 className="security-step-title">Data Encryption</h4>
                      <p className="security-step-description">
                      We implement end-to-end encryption for all data, both in transit and at rest, using industry-standard 
                      encryption protocols to ensure that your sensitive information remains protected at all times.
                      </p>
                    </div>
                </div>

                <div className="gradient-hr-f"></div>

                <div className="security-principle-step">
                  <div className="security-step-number">03</div>
                    <div className='security-step-text'>
                      <h4 className="security-step-title">Authentication & Access</h4>
                      <p className="security-step-description">
                      Our platform features robust authentication mechanisms including multi-factor authentication, role-based 
                      access controls, and least privilege principles to prevent unauthorized access to sensitive resources.
                      </p>
                    </div>
                </div>
                
                <div className="gradient-hr-f"></div>

                <div className="security-principle-step">
                  <div className="security-step-number">04</div>
                    <div className='security-step-text'>
                      <h4 className="security-step-title">Incident Response</h4>
                      <p className="security-step-description">
                      We maintain a comprehensive incident response plan with trained security personnel ready to identify, 
                      contain, and remediate security incidents quickly to minimize potential impact.
                      </p>
                    </div>
                </div>
                <div className="gradient-hr-f"></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* security Process Section */}
      <div className="py-20 Security-process-new">
        <PathProgressScroller
          steps={securitySteps}
          svgPath={SAMPLE_PATHS.custom}
          svgViewBox="0 0 1100 300"
        />
      </div>
      <div className="security-process-section">
        <div className="security-process-section-content">
          <div className='sections-title-f'>
              <p className="sections-title">Our Security Process</p>
          </div>
        </div>
        <div className='security-process-section-grid'>
          {securitySteps.map((step) => (
            <div className="content-f " key={step.id}>
              <div className='step-circle'>
                <img 
                  src={step.activeicon} 
                  alt={step.title} 
                />         
              </div>
              <p className='content-f-heading'>{step.title}</p>
              <p className='content-f-des'>
                {step.description}
              </p>
            </div>
          ))}
        </div>
      </div>



      {/*  Alyternatice security Process Section */}
      {/* <div className="security-process-section">
        <div className='sections-title-f'>
            <p className="sections-title">Our Security Process</p>
        </div>
        <div className="security-process">
          <div className="wave-background">
            <div className='step-circle one'>
              <div className='number-circle'>1</div>
              <div className="icon-circle"><TriangleAlert size={30}/></div>
            </div>
            <div className='step-circle two'>
              <div className='number-circle'>2</div>
              <div className="icon-circle two"><Lock size={30}/></div>
            </div>
            <div className='step-circle three'>
              <div className='number-circle'>3</div>
              <div className="icon-circle three"><Laptop2 size={30}/></div>
            </div>
            <div className='step-circle four'>
              <div className='number-circle'>4</div>
              <div className="icon-circle four"><FileSearch size={30}/></div>
            </div>
            <div className="content-f one" >
              <p className='content-f-heading'>Threat Assessment</p>
              <p className='content-f-des'>
                We continuously monitor for emerging threats and vulnerabilities, performing
                regular risk assessments to identify potential security gaps in our systems and
                applications.
              </p>
            </div>
            <div className="content-f two" >
              <p className='content-f-heading'>Security Testing</p>
              <p className='content-f-des'>
                Our applications undergo rigorous security testing including static code analysis,
                dynamic application scanning, and regular penetration tests performed by certified
                security professionals.
              </p>
            </div>
            <div className="content-f three" >
              <p className='content-f-heading'>Continuous Monitoring</p>
              <p className='content-f-des'>
                We employ advanced monitoring tools and automated alerts to detect and respond to
                suspicious activities and potential security incidents in real-time across all our systems.
              </p>
            </div>
            <div className="content-f four" >
              <p className='content-f-heading'>Regular Audits</p>
              <p className='content-f-des'>
                We conduct regular internal and external security audits to verify compliance with
                security policies, industry standards, and regulatory requirements, ensuring continued
                protection of your data.
              </p>
            </div>
          </div>
        </div>
      </div> */}


      {/* security connect Section */}
      <div className='security-connect-section'>
        <div className='security-connect'>
          <div className='security-connect-content'>
            <p className='content-title content-title-f'>Ready to Secure Your Business?</p>
            <p className='content-text content-text-f'>At ReventLabs, we prioritize the security and privacy of your data. Our comprehensive security framework ensures your information remains protected while enabling your business to leverage our powerful services. Contact us today to learn more about how we can help secure your operations.</p>
            <ContactButton variant="border" className="text-black">Contact Our Security Team</ContactButton>
          </div>
            <img src={image2} alt='Security team illustration'></img>
        </div>
      </div>

      {/* security Number Section */}
      <div className='security-number-section'>
        <div className='security-number'>
          <p className='sections-title'>Security by the Numbers</p>
          <div className="metrics-section-f">
            <div className="metrics-container-f">
              <div className="grid-divider"></div>
              <div className="metric-item">
                <div className="metrics-value">99.9%</div>
                <div className="metrics-label">Uptime Reliability</div>
              </div>
              <div className="grid-divider"></div>

              <div className="metric-item">
                <div className="metrics-value">24/7</div>
                <div className="metrics-label">Security Monitoring</div>
              </div>
              <div className="grid-divider"></div>

              <div className="metric-item">
                <div className="metrics-value">100%</div>
                <div className="metrics-label">Data Encryption</div>
              </div>
              <div className="grid-divider"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SecurityPage; 